/**
 * KAIROS OS — Supabase Data Hooks com Realtime
 * Cada hook: carrega dados + subscrição em tempo real
 * Quando há INSERT/UPDATE/DELETE no Supabase → app actualiza automaticamente
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabase';

// ─── Tipo base ───────────────────────────────────────────────
export interface DbResult<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

// ─── Hook genérico com Realtime ──────────────────────────────
export function useTable<T = any>(
  table: string,
  options?: {
    orderBy?: string;
    ascending?: boolean;
    filter?: Record<string, any>;
    realtime?: boolean; // activar realtime (default: true)
    enabled?: boolean;  // se false, não faz fetch nem cria canal (default: true)
  }
): DbResult<T> & {
  insert: (row: Partial<T>) => Promise<T | null>;
  update: (id: string, changes: Partial<T>) => Promise<T | null>;
  remove: (id: string) => Promise<boolean>;
  upsert: (row: Partial<T>) => Promise<T | null>;
} {
  const [data, setData]       = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const channelRef            = useRef<any>(null);
  const enableRealtime        = options?.realtime !== false; // default true
  const enabled               = options?.enabled !== false;  // default true

  // ── Carregar dados ─────────────────────────────────────────
  const load = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      let q = supabase.from(table).select('*');
      if (options?.filter) {
        Object.entries(options.filter).forEach(([k, v]) => {
          q = q.eq(k, v) as any;
        });
      }
      if (options?.orderBy) {
        q = q.order(options.orderBy, { ascending: options?.ascending ?? false }) as any;
      }
      const { data: rows, error: err } = await q;
      if (err) throw err;
      setData(rows || []);
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [table, enabled]); // eslint-disable-line

  // ── Subscrição Realtime ────────────────────────────────────
  useEffect(() => {
    if (!enabled) return;
    load();

    if (!enableRealtime) return;

    // Canal único por instância — Supabase exige nomes únicos por subscrição
    // (canais partilhados causam falhas silenciosas)
    const channelName = `realtime:${table}:${Math.random().toString(36).slice(2)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table },
        (payload: any) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;

          setData(prev => {
            switch (eventType) {
              case 'INSERT':
                // Adicionar só se não existir já (evitar duplicados)
                if (prev.find((r: any) => r.id === newRecord.id)) return prev;
                return [newRecord, ...prev];

              case 'UPDATE':
                return prev.map((r: any) =>
                  r.id === newRecord.id ? newRecord : r
                );

              case 'DELETE':
                return prev.filter((r: any) => r.id !== oldRecord.id);

              default:
                return prev;
            }
          });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [table, enableRealtime]); // eslint-disable-line

  // ── Operações CRUD ─────────────────────────────────────────
  const insert = async (row: Partial<T>): Promise<T | null> => {
    try {
      // Preencher user_id automaticamente se a row não o tiver
      // (as tabelas têm user_id NOT NULL + RLS auth.uid()=user_id)
      let rowToInsert: any = { ...row };
      if (!rowToInsert.user_id) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) rowToInsert.user_id = user.id;
      }
      const { data: inserted, error: err } = await supabase
        .from(table).insert([rowToInsert]).select().single();
      if (err) throw err;
      // Realtime vai actualizar automaticamente — mas se não estiver activo, actualizar manualmente
      if (!enableRealtime) {
        setData(d => [inserted, ...d] as T[]);
      }
      return inserted as T;
    } catch (e: any) {
      setError(e.message);
      console.error(`Erro insert ${table}:`, e.message);
      return null;
    }
  };

  const update = async (id: string, changes: Partial<T>): Promise<T | null> => {
    try {
      const { data: updated, error: err } = await supabase
        .from(table).update(changes).eq('id', id).select().single();
      if (err) throw err;
      if (!enableRealtime) {
        setData(d => d.map((r: any) => r.id === id ? updated : r) as T[]);
      }
      return updated as T;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  };

  const remove = async (id: string): Promise<boolean> => {
    try {
      const { error: err } = await supabase.from(table).delete().eq('id', id);
      if (err) throw err;
      if (!enableRealtime) {
        setData(d => d.filter((r: any) => r.id !== id));
      }
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  };

  const upsert = async (row: Partial<T>): Promise<T | null> => {
    try {
      const { data: upserted, error: err } = await supabase
        .from(table).upsert([row]).select().single();
      if (err) throw err;
      if (!enableRealtime) {
        setData(d => {
          const exists = d.findIndex((r: any) => r.id === (row as any).id);
          if (exists >= 0) return d.map((r: any, i: number) => i === exists ? upserted : r) as T[];
          return [upserted, ...d] as T[];
        });
      }
      return upserted as T;
    } catch (e: any) {
      setError(e.message);
      return null;
    }
  };

  return { data, loading, error, reload: load, insert, update, remove, upsert };
}

// ─── Hooks específicos — todos com Realtime activo ───────────

// Home Base
export const useHBTransacoes  = () => useTable('hb_transacoes',  { orderBy: 'data',       ascending: false, realtime: false });
export const useHBObjetivos   = () => useTable('hb_objetivos',   { orderBy: 'created_at', realtime: false });
export const useHBWishlist    = () => useTable('hb_wishlist',    { orderBy: 'prioridade', realtime: false });
export const useHBPagamentos  = () => useTable('hb_pagamentos',  { orderBy: 'nome',        realtime: false });
export const useHBTarefas     = () => useTable('hb_tarefas',     { orderBy: 'created_at', ascending: false, realtime: false });
export const useHBPatrimonios = () => useTable('hb_patrimonios', { orderBy: 'ordem',       realtime: false });
export const useHBMovimentos  = (patrimonioId?: string) =>
  useTable('hb_movimentos', {
    orderBy: 'data', ascending: false,
    filter: patrimonioId ? { patrimonio_id: patrimonioId } : undefined,
  });
export const useHBDividas     = () => useTable('hb_dividas',     { orderBy: 'created_at', ascending: false, realtime: false });
export const useHBPagamentosDiv = (dividaId?: string) =>
  useTable('hb_pagamentos_divida', {
    orderBy: 'data', ascending: false,
    filter: dividaId ? { divida_id: dividaId } : undefined,
  });

// AL — Quinta da Regada (Realtime crítico — reservas vêm do sync iCal)
export const useALCasas      = () => useTable('al_casas',      { orderBy: 'ordem',       realtime: false });
export const useALReservas   = () => useTable('al_reservas',   { orderBy: 'check_in',   ascending: false });
export const useALLimpezas   = () => useTable('al_limpezas',   { orderBy: 'data',       ascending: false });
export const useALManutencao = () => useTable('al_manutencao', { orderBy: 'data',       ascending: false, realtime: false });
export const useALTarefas    = () => useTable('al_tarefas',    { orderBy: 'created_at', ascending: false, realtime: false });
export const useALDividas    = () => useTable('al_dividas',    { orderBy: 'created_at', ascending: true,  realtime: false });
export const useKairosNotificacoes = () => useTable('kairos_notificacoes', { orderBy: 'created_at', ascending: false, realtime: true });

// VJ Casamentos
export const useVJEventos  = () => useTable('vj_eventos',  { orderBy: 'data',       ascending: false });
export const useVJDespesas = () => useTable('vj_despesas', { orderBy: 'data',       ascending: false, realtime: false });
export const useVJTarefas  = () => useTable('vj_tarefas',  { orderBy: 'created_at', ascending: false, realtime: false });
export const useVJConfig   = () => useTable('vj_config',   { realtime: false        }); // config muda raramente

// HW3D
export const useHW3DClientes = () => useTable('hw3d_clientes', { orderBy: 'nome',         realtime: false });
export const useHW3DProjetos = () => useTable('hw3d_projetos', { orderBy: 'created_at',  ascending: false, realtime: false });
export const useHW3DDespesas = () => useTable('hw3d_despesas', { orderBy: 'data',        ascending: false, realtime: false });
export const useHW3DTarefas  = () => useTable('hw3d_tarefas',  { orderBy: 'created_at',  ascending: false, realtime: false });

// Kairos AI
export const useKaiClientes   = () => useTable('kai_clientes',   { orderBy: 'nome',         realtime: false });
export const useKaiProjetos   = () => useTable('kai_projetos',   { orderBy: 'created_at',  ascending: false, realtime: false });
export const useKaiRetainers  = () => useTable('kai_retainers',  { orderBy: 'created_at',  ascending: false, realtime: false });
export const useKaiDespesas   = () => useTable('kai_despesas',   { orderBy: 'data',        ascending: false, realtime: false });
export const useKaiTarefas    = () => useTable('kai_tarefas',    { orderBy: 'created_at',  ascending: false, realtime: false });
export const useKaiCalendario = () => useTable('kai_calendario', { orderBy: 'data',         realtime: false });

// PhotoBooth
export const usePBEventos  = () => useTable('pb_eventos',  { orderBy: 'data',       ascending: false, realtime: false });
export const usePBDespesas = () => useTable('pb_despesas', { orderBy: 'data',       ascending: false, realtime: false });
export const usePBTarefas  = () => useTable('pb_tarefas',  { orderBy: 'created_at', ascending: false, realtime: false });

// Holdings
export const useHoldingsCalendario = () => useTable('holdings_calendario', { orderBy: 'data', realtime: false });
export const useLogsDB = (empresa?: string) =>
  useTable('logs', {
    orderBy: 'created_at', ascending: false,
    filter: empresa ? { empresa } : undefined,
  });


// VJ — Equipa e Colaboradores
export const useVJPerfil      = () => useTable('vj_perfil',      { realtime: false });
export const useVJEquipa      = () => useTable('vj_equipa',      { realtime: false });
export const useVJAtribuicoes = () => useTable('vj_atribuicoes', { orderBy: 'created_at', ascending: false });

// ─── Helper: adicionar log ───────────────────────────────────
export async function addLogDB(
  empresa: string,
  tipo: string,
  descricao: string,
  icon?: string,
  valor?: number
): Promise<void> {
  try {
    await supabase.from('logs').insert([{
      empresa, tipo, descricao,
      icon: icon || '📋',
      valor: valor || 0,
    }]);
  } catch (_) {}
}

// ─── Helper: perfil do utilizador ───────────────────────────
export async function getProfile(): Promise<any> {
  const { data } = await supabase.from('profiles').select('*').single();
  return data;
}

export async function upsertProfile(changes: Record<string, any>): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  await supabase.from('profiles').upsert({ id: user.id, ...changes });
}
